import { Link, useParams } from 'react-router';

import { ROUTES } from '#/config/routes';
import { fetchStockAdjustmentDetail } from '#/api/imsMgmt/receipts/stockAdjustment/fetchDetail';
import { updateStockAdjustmentRemark } from '#/api/imsMgmt/receipts/stockAdjustment/updateRemark';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useDocumentRemarkSave from '#/hooks/useDocumentRemarkSave';
import DocumentRemarkEditForm from '#/modules/imsMgmt/receipts/shared/DocumentRemarkEditForm';
import StockAdjustmentMainInfoCard from '#/modules/imsMgmt/receipts/stockAdjustment/components/StockAdjustmentMainInfoCard';
import {
  getStockAdjustmentApiErrorMessage,
  isStockAdjustmentVersionConflict,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/apiError';
import { canEditStockAdjustmentRemark } from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentStatus';
import {
  stockAdjustmentDetailQueryKey,
  stockAdjustmentListQueryKeyPrefix,
} from '#/utils/queryKeys';

const StockAdjustmentRemarkPage = () => {
  const { id } = useParams();

  const {
    detail,
    detailQuery,
    isPending,
    saveRemark,
  } = useDocumentRemarkSave({
    id,
    fetchDetail: fetchStockAdjustmentDetail,
    updateRemark: updateStockAdjustmentRemark,
    detailQueryKey: stockAdjustmentDetailQueryKey,
    listQueryKeyPrefix: stockAdjustmentListQueryKeyPrefix,
    getErrorMessage: (error) => getStockAdjustmentApiErrorMessage(error, '更新備註失敗'),
    isVersionConflict: isStockAdjustmentVersionConflict,
    afterSaveNavigateTo: ROUTES.stockAdjustmentDetail(id),
  });

  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;
  const remarkAllowed = detail ? canEditStockAdjustmentRemark(detail.status) : false;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">編輯調整備註</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.stockAdjustmentDetail(id)}>返回詳情</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getStockAdjustmentApiErrorMessage(detailQuery.error, '找不到調整單')}
            </p>
          ) : null}

          {detail && !remarkAllowed ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">此狀態不可編輯備註。</p>
              <Button type="button" asChild>
                <Link to={ROUTES.stockAdjustmentDetail(id)}>前往詳情</Link>
              </Button>
            </div>
          ) : null}

          {detail && remarkAllowed ? (
            <>
              <StockAdjustmentMainInfoCard detail={detail} />
              <DocumentRemarkEditForm
                initialRemark={detail.remark ?? ''}
                cancelTo={ROUTES.stockAdjustmentDetail(id)}
                isPending={isPending}
                onSave={saveRemark}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default StockAdjustmentRemarkPage;
